import { useState } from 'react'
import { motion } from 'framer-motion'
import {
  Upload,
  FileText,
  CheckCircle,
  AlertTriangle,
  ArrowRight,
  ArrowLeft,
  X,
  FileCheck,
  Loader,
  Lock
} from 'lucide-react'
import EnhancedCard from '../../../components/common/EnhancedCard'
import { companyService } from '../../../services/companyService'

const DocumentUpload = ({ data, onNext, onPrevious, isLoading, bgvStatus = 'pending', rejectionReasons = {} }) => {
  const [documents, setDocuments] = useState({
    certificate_of_incorporation: data.documents?.certificate_of_incorporation || null,
    gst_registration_certificate: data.documents?.gst_registration_certificate || null,
    pan_card_copy: data.documents?.pan_card_copy || null,
    audited_financial_statements: data.documents?.audited_financial_statements || null,
    signed_nda: data.documents?.signed_nda || null,
    master_service_agreement: data.documents?.master_service_agreement || null
  })

  const [uploadStatus, setUploadStatus] = useState({})

  const requiredDocuments = [
    {
      id: 'certificate_of_incorporation',
      name: 'Certificate of Incorporation',
      description: 'Upload your certificate of incorporation (PDF, JPG, PNG)',
      required: true,
      acceptedTypes: ['pdf', 'jpg', 'png'],
      maxSize: '5MB'
    },
    {
      id: 'gst_registration_certificate',
      name: 'GST Registration Certificate',
      description: 'Upload your GST registration certificate',
      required: true,
      acceptedTypes: ['pdf', 'jpg', 'png'],
      maxSize: '5MB'
    },
    {
      id: 'pan_card_copy',
      name: 'PAN Card Copy',
      description: 'Upload company PAN card',
      required: true,
      acceptedTypes: ['pdf', 'jpg', 'png'],
      maxSize: '2MB'
    },
    {
      id: 'audited_financial_statements',
      name: 'Audited Financial Statements (Last 2 Years)',
      description: 'Upload audited financial statements',
      required: true,
      acceptedTypes: ['pdf', 'doc', 'docx'],
      maxSize: '5MB'
    },
    {
      id: 'signed_nda',
      name: 'Signed NDA',
      description: 'Upload signed NDA document',
      required: true,
      acceptedTypes: ['pdf', 'doc', 'docx'],
      maxSize: '5MB'
    },
    {
      id: 'master_service_agreement',
      name: 'Master Service Agreement',
      description: 'Upload master service agreement',
      required: true,
      acceptedTypes: ['pdf', 'doc', 'docx'],
      maxSize: '5MB'
    }
  ]

  const handleFileUpload = (documentId, file) => {
    // Validate file
    const doc = requiredDocuments.find(d => d.id === documentId)
    if (!doc) return

    const fileExtension = file.name.split('.').pop().toLowerCase()

    // Parse max size from string "5MB" -> 5
    const maxSizeVal = parseFloat(doc.maxSize.replace(/[^0-9.]/g, ''))
    const fileSizeMB = file.size / (1024 * 1024)

    if (!doc.acceptedTypes.includes(fileExtension)) {
      setUploadStatus(prev => ({
        ...prev,
        [documentId]: { status: 'error', message: `File type not supported. Accepted: ${doc.acceptedTypes.join(', ')}` }
      }))
      return
    }

    if (fileSizeMB > maxSizeVal) {
      setUploadStatus(prev => ({
        ...prev,
        [documentId]: { status: 'error', message: `File size too large. Maximum: ${doc.maxSize}` }
      }))
      return
    }

    // Real upload: call companyService.uploadDocument immediately
    setUploadStatus(prev => ({
      ...prev,
      [documentId]: { status: 'uploading', message: 'Uploading...' }
    }))

    // Derive company/user IDs from props or session to avoid ReferenceError
    const companyId = data?.company_id || data?.companyId || (typeof window !== 'undefined' ? (() => {
      try { const v = sessionStorage.getItem('companyId'); return v ? JSON.parse(v) : null } catch (e) { return null }
    })() : null)
    const userId = data?.user_id || data?.userId || (typeof window !== 'undefined' ? (() => {
      try { const v = sessionStorage.getItem('userId'); return v ? JSON.parse(v) : null } catch (e) { return null }
    })() : null)

    console.debug('Uploading document', documentId, { companyId, userId, fileName: file.name })

    const fd = new FormData()
    fd.append('document_type', documentId)
    fd.append('file', file)
    if (companyId) fd.append('company_id', String(companyId))
    if (userId) fd.append('user_id', String(userId))

    companyService.uploadDocument(fd)
      .then((res) => {
        // Keep the file in UI state but mark upload success
        setDocuments(prev => ({
          ...prev,
          [documentId]: file
        }))

        setUploadStatus(prev => ({
          ...prev,
          [documentId]: { status: 'success', message: 'Upload successful', response: res }
        }))
      })
      .catch((err) => {
        console.error('Upload error for', documentId, err)
        setUploadStatus(prev => ({
          ...prev,
          [documentId]: { status: 'error', message: err?.response?.data?.message || err?.message || 'Upload failed' }
        }))
      })
  }

  const handleFileChange = (documentId, event) => {
    const file = event.target.files[0]
    if (file) {
      handleFileUpload(documentId, file)
    }
  }

  const removeFile = (documentId) => {
    setDocuments(prev => ({
      ...prev,
      [documentId]: null
    }))

    setUploadStatus(prev => ({
      ...prev,
      [documentId]: null
    }))
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-success-500" />
      case 'error':
        return <AlertTriangle className="w-5 h-5 text-error-500" />
      case 'uploading':
        return <Loader className="w-5 h-5 animate-spin text-primary-500" />
      default:
        return null
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'text-success-600 bg-success-100'
      case 'error':
        return 'text-error-600 bg-error-100'
      case 'uploading':
        return 'text-primary-600 bg-primary-100'
      default:
        return 'text-gray-600 bg-gray-100'
    }
  }

  const isAllRequiredUploaded = () => {
    // If in fix mode, we only need to have values for items that are editable (rejected ones)
    // But conceptually, we should have ALL documents present.
    // documents state should contain previous accepted files + new fixed files.
    // So logic remains: Every required doc must have a value.
    return requiredDocuments.every(doc =>
      !doc.required || documents[doc.id] !== null
    )
  }

  const handleNext = () => {
    if (isAllRequiredUploaded()) {
      onNext({ documents })
    }
  }

  // Helper to determine document state based on global status
  const getDocumentState = (docId) => {
    // rejectionReasons is object { "docId": "Reason" }
    const reason = rejectionReasons[docId]

    if (bgvStatus === 'pending') return { mode: 'edit', message: null }

    if (bgvStatus === 'uploaded' || bgvStatus === 'in_progress') {
      return { mode: 'locked', message: 'Under Review', status: 'pending' }
    }

    if (bgvStatus === 'verified' || bgvStatus === 'approved') {
      return { mode: 'locked', message: 'Approved', status: 'approved' }
    }

    if (bgvStatus === 'verification_failed' || bgvStatus === 'failed') {
      if (reason) {
        return { mode: 'edit', message: `Rejected: ${reason}`, status: 'rejected' }
      } else {
        // If not explicitly rejected, assume approved? Or locked waiting for others?
        return { mode: 'locked', message: 'Approved', status: 'approved' }
      }
    }

    return { mode: 'edit', message: null }
  }

  const isEditable = (docState) => docState.mode === 'edit'

  return (
    <div className="max-w-6xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
        {/* Documents List */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {requiredDocuments.map((doc, index) => {
            const file = documents[doc.id]
            const upStatus = uploadStatus[doc.id]
            const docState = getDocumentState(doc.id)
            const locked = !isEditable(docState)

            return (
              <motion.div
                key={doc.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: index * 0.05 }}
              >
                <EnhancedCard className={`p-5 h-full flex flex-col justify-between hover:shadow-lg transition-all duration-200 ${docState.status === 'rejected' ? 'border-red-200 bg-red-50/10' : ''}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start space-x-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border shadow-sm ${docState.status === 'rejected' ? 'bg-red-50 border-red-100 text-red-500' :
                          docState.status === 'approved' ? 'bg-green-50 border-green-100 text-green-600' :
                            'bg-gray-50 border-gray-100 text-primary-600'
                        }`}>
                        {docState.status === 'rejected' ? <AlertTriangle className="w-5 h-5" /> :
                          docState.status === 'approved' ? <CheckCircle className="w-5 h-5" /> :
                            <FileText className="w-5 h-5" />}
                      </div>
                      <div className="min-w-0 pt-0.5">
                        <h3 className="font-semibold text-gray-900 text-sm truncate pr-2">
                          {doc.name}
                          {doc.required && <span className="text-red-500 ml-1">*</span>}
                        </h3>
                        <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">{doc.description}</p>
                        {docState.message && (
                          <p className={`text-xs mt-1 font-medium ${docState.status === 'rejected' ? 'text-red-600' :
                              docState.status === 'approved' ? 'text-green-600' : 'text-blue-600'
                            }`}>
                            {docState.message}
                          </p>
                        )}
                      </div>
                    </div>
                    {locked && docState.status === 'approved' && (
                      <CheckCircle className="w-5 h-5 text-green-500 shrink-0" />
                    )}
                    {docState.status === 'rejected' && (
                      <div className="w-2 h-2 rounded-full bg-red-500 shrink-0 mt-1"></div>
                    )}
                  </div>

                  {file ? (
                    <div className="mt-2 group relative">
                      <div className={`flex items-center p-3 rounded-lg border ${docState.status === 'rejected' ? 'bg-white border-red-200' : 'bg-gray-50 border-gray-200'
                        }`}>
                        <FileCheck className={`w-5 h-5 shrink-0 mr-3 ${docState.status === 'rejected' ? 'text-red-500' : 'text-primary-600'}`} />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 text-sm truncate">{file.name}</p>
                          <p className="text-xs text-gray-500">{(file.size / (1024 * 1024)).toFixed(2)} MB</p>
                        </div>
                        {!locked && (
                          <button
                            onClick={() => removeFile(doc.id)}
                            className="p-1.5 text-gray-400 hover:text-red-500 transition-colors rounded-full hover:bg-gray-100"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                        {locked && <Lock className="w-4 h-4 text-gray-400" />}
                      </div>
                    </div>
                  ) : (
                    <div className="mt-2">
                      {!locked ? (
                        <>
                          <input
                            type="file"
                            accept={doc.acceptedTypes.map(type => `.${type}`).join(',')}
                            onChange={(e) => handleFileChange(doc.id, e)}
                            className="hidden"
                            id={`upload-${doc.id}`}
                            disabled={locked}
                          />
                          <label
                            htmlFor={`upload-${doc.id}`}
                            className={`flex flex-col items-center justify-center w-full px-4 py-4 border-2 border-dashed rounded-xl transition-all cursor-pointer group ${docState.status === 'rejected' ? 'border-red-300 bg-red-50/50 hover:bg-red-50' :
                                'border-gray-200 hover:border-primary-400 hover:bg-primary-50/50'
                              }`}
                          >
                            <Upload className={`w-5 h-5 mb-2 transition-colors ${docState.status === 'rejected' ? 'text-red-400 group-hover:text-red-500' : 'text-gray-400 group-hover:text-primary-500'
                              }`} />
                            <span className={`text-sm font-medium transition-colors ${docState.status === 'rejected' ? 'text-red-600' : 'text-gray-600 group-hover:text-primary-700'
                              }`}>
                              {upStatus?.status === 'uploading' ? 'Uploading...' : docState.status === 'rejected' ? 'Re-upload File' : 'Click to Upload'}
                            </span>
                            <span className="text-[10px] text-gray-400 mt-1 group-hover:text-primary-400/80 transition-colors">
                              {doc.acceptedTypes.join(', ').toUpperCase()} • Max {doc.maxSize}
                            </span>
                          </label>
                        </>
                      ) : (
                        <div className="flex flex-col items-center justify-center w-full px-4 py-4 border border-gray-100 bg-gray-50 rounded-xl">
                          <span className="text-xs text-gray-500 italic">Upload locked</span>
                        </div>
                      )}
                    </div>
                  )}
                </EnhancedCard>
              </motion.div>
            )
          })}
        </div>

        {/* Footer Summary & Nav */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div className="flex items-center space-x-4">
            <div className="text-sm text-gray-600">
              <span className="font-medium">Progress:</span> <span className="font-bold text-gray-900">{Object.values(documents).filter(d => d).length}/{requiredDocuments.length}</span> documents uploaded
            </div>
          </div>

          <div className="flex space-x-3">
            <button
              onClick={onPrevious}
              className="px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-xl transition-colors border border-gray-200"
            >
              Back
            </button>

            {/* Show submit button if editable */}
            {bgvStatus === 'pending' || bgvStatus === 'verification_failed' ? (
              <button
                onClick={handleNext}
                disabled={!isAllRequiredUploaded() || isLoading}
                className="px-6 py-2.5 text-sm font-medium bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-50 shadow-md hover:shadow-lg"
              >
                {bgvStatus === 'verification_failed' ? 'Resubmit for Review' : 'Submit for Review'}
              </button>
            ) : (
              <div className="text-sm text-gray-500 italic px-4 py-2">
                Documents under review
              </div>
            )}

          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default DocumentUpload
